'use client';

import { useState, useMemo, useCallback } from 'react';
import { 
  TickSnapshot, 
  ScreenerFilter, 
  FilterOperator, 
  FILTERABLE_FIELDS,
  ESMFilterOption,
  GSMFilterOption 
} from '@/types/tickSnapshot';
import { ArrowDown, ArrowUp, Download, Search, X, Plus, Filter, Eye, AlertTriangle } from 'lucide-react';
import StockDetailModal from '../modals/StockDetailModal';
import TickSnapshotDetailModal from './TickSnapshotDetailModal';
import Button from '@/components/ui/Button';
import StockActionIcons from '../stocks/StockActionIcons';

interface TickAnalysisTableProps {
  snapshots: TickSnapshot[];
}

type SortField = 
  | 'trading_symbol' 
  | 'last_price' 
  | 'volume_traded' 
  | 'net_change'
  | 'change_percent'
  | 'total_buy_quantity' 
  | 'total_sell_quantity'
  | 'buy_sell_ratio'
  | 'average_trade_price'
  | 'ohlc_open'
  | 'ohlc_high'
  | 'ohlc_low'
  | 'ohlc_close'
  | 'market_cap'
  | 'price_to_earning'
  | 'return_1day'
  | 'return_1week'
  | 'return_1month'
  | 'bid_ask_spread'
  | 'esm_stage'
  | 'gsm_stage';

type SortDirection = 'asc' | 'desc';

// Helper function to get nested value from object using dot notation
const getNestedValue = (obj: Record<string, unknown>, path: string): number | null => {
  const parts = path.split('.');
  let current: unknown = obj;
  
  for (const part of parts) {
    if (current === null || current === undefined) return null;
    if (typeof current !== 'object') return null;
    current = (current as Record<string, unknown>)[part];
  }
  
  return typeof current === 'number' ? current : null;
};

// Generate unique ID for filters
const generateFilterId = () => `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export default function TickAnalysisTable({ snapshots }: TickAnalysisTableProps) {
  const [selectedStock, setSelectedStock] = useState<string | null>(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState<TickSnapshot | null>(null);
  const [sortField, setSortField] = useState<SortField>('volume_traded');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ScreenerFilter[]>([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  // ESM/GSM filter states
  const [esmFilter, setEsmFilter] = useState<ESMFilterOption>('all');
  const [gsmFilter, setGsmFilter] = useState<GSMFilterOption>('all');
  
  // New filter input state
  const [newFilterField, setNewFilterField] = useState(FILTERABLE_FIELDS[0].key);
  const [newFilterOperator, setNewFilterOperator] = useState<FilterOperator>('>');
  const [newFilterValue, setNewFilterValue] = useState('');

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Format large numbers (e.g., 1.23K, 4.56L, 5.67Cr)
  const formatNumber = useCallback((value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const sign = isNegative ? '-' : '';
    
    if (absValue >= 10000000) return `${sign}${(absValue / 10000000).toFixed(2)} Cr`;
    if (absValue >= 100000) return `${sign}${(absValue / 100000).toFixed(2)} L`;
    if (absValue >= 1000) return `${sign}${(absValue / 1000).toFixed(2)}K`;
    return `${sign}${absValue.toFixed(2)}`;
  }, []);

  // Format volume as full number with locale separators
  const formatVolume = useCallback((value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    return Math.abs(value).toLocaleString('en-IN');
  }, []);

  // Format percentage with color coding
  const formatPercentage = useCallback((value: number | null | undefined) => {
    if (value === null || value === undefined) return { text: 'N/A', color: 'text-zinc-700 dark:text-zinc-300' };
    
    const absValue = Math.abs(value);
    let color = 'text-zinc-700 dark:text-zinc-300';
    
    if (value > 0) {
      if (absValue >= 5) color = 'text-green-700 dark:text-green-400 font-semibold';
      else if (absValue >= 2) color = 'text-green-600 dark:text-green-500';
      else color = 'text-green-500 dark:text-green-400';
    } else if (value < 0) {
      if (absValue >= 5) color = 'text-red-700 dark:text-red-400 font-semibold';
      else if (absValue >= 2) color = 'text-red-600 dark:text-red-500';
      else color = 'text-red-500 dark:text-red-400';
    }
    
    return {
      text: `${value > 0 ? '+' : ''}${value.toFixed(2)}%`,
      color
    };
  }, []);

  // Calculate derived values for a snapshot
  const getDerivedValues = useCallback((snapshot: TickSnapshot) => {
    const tickData = snapshot.tick_data;
    const ohlc = tickData?.ohlc;
    const depth = tickData?.depth;
    const financial = snapshot.financial_info;
    
    const changePercent = ohlc?.close && ohlc.close !== 0 
      ? ((snapshot.last_price - ohlc.close) / ohlc.close) * 100 
      : 0;
    
    const buySellRatio = tickData?.total_sell_quantity && tickData.total_sell_quantity > 0
      ? tickData.total_buy_quantity / tickData.total_sell_quantity
      : null;
    
    const bestBidPrice = depth?.buy?.[0]?.price ?? null;
    const bestAskPrice = depth?.sell?.[0]?.price ?? null;
    const bidAskSpread = bestBidPrice && bestAskPrice 
      ? ((bestAskPrice - bestBidPrice) / bestBidPrice) * 100 
      : null;
    
    return {
      changePercent,
      buySellRatio,
      bidAskSpread,
      bestBidPrice,
      bestAskPrice,
      marketCap: financial?.market_capitalization ?? null,
      pe: financial?.price_to_earning ?? null,
      return1d: financial?.return_over_1day ?? null,
      return1w: financial?.return_over_1week ?? null,
      return1m: financial?.return_over_1month ?? null,
    };
  }, []);

  // Apply a single filter to a snapshot
  const applyFilter = useCallback((snapshot: TickSnapshot, filter: ScreenerFilter): boolean => {
    let value: number | null = null;
    
    // Handle special calculated fields
    if (filter.field === 'buy_sell_qty_ratio') {
      const tickData = snapshot.tick_data;
      if (tickData?.total_sell_quantity && tickData.total_sell_quantity > 0) {
        value = tickData.total_buy_quantity / tickData.total_sell_quantity;
      }
    } else if (filter.field === 'bid_ask_spread') {
      const depth = snapshot.tick_data?.depth;
      const bestBidPrice = depth?.buy?.[0]?.price;
      const bestAskPrice = depth?.sell?.[0]?.price;
      if (bestBidPrice && bestAskPrice) {
        value = ((bestAskPrice - bestBidPrice) / bestBidPrice) * 100;
      }
    } else if (filter.field === 'change_percent') {
      const ohlc = snapshot.tick_data?.ohlc;
      if (ohlc?.close && ohlc.close !== 0) {
        value = ((snapshot.last_price - ohlc.close) / ohlc.close) * 100;
      }
    } else {
      // Get value from nested path
      value = getNestedValue(snapshot as unknown as Record<string, unknown>, filter.field);
    }
    
    if (value === null) return false;
    
    switch (filter.operator) {
      case '>': return value > filter.value;
      case '<': return value < filter.value;
      case '>=': return value >= filter.value;
      case '<=': return value <= filter.value;
      case '=': return Math.abs(value - filter.value) < 0.001;
      case '!=': return Math.abs(value - filter.value) >= 0.001;
      default: return true;
    }
  }, []);

  // Get filtered and sorted snapshots
  const filteredAndSortedSnapshots = useMemo(() => {
    // First filter by search query
    let filtered = snapshots.filter(snapshot => {
      if (!searchQuery) return true;
      
      const query = searchQuery.toLowerCase();
      const symbol = snapshot.trading_symbol?.toLowerCase() || '';
      const stockName = snapshot.stock_info?.stock_name?.toLowerCase() || '';
      const industry = snapshot.stock_info?.industry_name?.toLowerCase() || '';
      const sector = snapshot.stock_info?.sector_name?.toLowerCase() || '';
      const nseCode = snapshot.stock_info?.nse_code?.toLowerCase() || '';
      
      return symbol.includes(query) || 
             stockName.includes(query) || 
             industry.includes(query) ||
             sector.includes(query) ||
             nseCode.includes(query);
    });
    
    // Apply ESM filter
    if (esmFilter !== 'all') {
      filtered = filtered.filter(snapshot => {
        const esmInfo = snapshot.esm_info;
        switch (esmFilter) {
          case 'in_esm':
            return esmInfo?.is_in_esm === true;
          case 'not_in_esm':
            return !esmInfo?.is_in_esm;
          case 'esm_1':
            return esmInfo?.is_in_esm && esmInfo.current_stage === 1;
          case 'esm_2':
            return esmInfo?.is_in_esm && esmInfo.current_stage === 2;
          default:
            return true;
        }
      });
    }
    
    // Apply GSM filter
    if (gsmFilter !== 'all') {
      filtered = filtered.filter(snapshot => {
        const gsmInfo = snapshot.gsm_info;
        switch (gsmFilter) {
          case 'in_gsm':
            return gsmInfo?.is_in_gsm === true;
          case 'not_in_gsm':
            return !gsmInfo?.is_in_gsm;
          default:
            return true;
        }
      });
    }
    
    // Then apply screener filters
    if (filters.length > 0) {
      filtered = filtered.filter(snapshot => 
        filters.every(filter => applyFilter(snapshot, filter))
      );
    }
    
    // Then sort the filtered snapshots
    return filtered.sort((a, b) => {
      let aValue: number | string | null = null;
      let bValue: number | string | null = null;
      
      const aDerived = getDerivedValues(a);
      const bDerived = getDerivedValues(b);

      switch (sortField) {
        case 'trading_symbol':
          aValue = a.trading_symbol;
          bValue = b.trading_symbol;
          break;
        case 'last_price':
          aValue = a.last_price;
          bValue = b.last_price;
          break;
        case 'volume_traded':
          aValue = a.volume_traded;
          bValue = b.volume_traded;
          break;
        case 'net_change':
          aValue = a.tick_data?.net_change ?? 0;
          bValue = b.tick_data?.net_change ?? 0;
          break;
        case 'change_percent':
          aValue = aDerived.changePercent;
          bValue = bDerived.changePercent;
          break;
        case 'total_buy_quantity':
          aValue = a.tick_data?.total_buy_quantity ?? 0;
          bValue = b.tick_data?.total_buy_quantity ?? 0;
          break;
        case 'total_sell_quantity':
          aValue = a.tick_data?.total_sell_quantity ?? 0;
          bValue = b.tick_data?.total_sell_quantity ?? 0;
          break;
        case 'buy_sell_ratio':
          aValue = aDerived.buySellRatio;
          bValue = bDerived.buySellRatio;
          break;
        case 'average_trade_price':
          aValue = a.tick_data?.average_trade_price ?? 0;
          bValue = b.tick_data?.average_trade_price ?? 0;
          break;
        case 'ohlc_open':
          aValue = a.tick_data?.ohlc?.open ?? 0;
          bValue = b.tick_data?.ohlc?.open ?? 0;
          break;
        case 'ohlc_high':
          aValue = a.tick_data?.ohlc?.high ?? 0;
          bValue = b.tick_data?.ohlc?.high ?? 0;
          break;
        case 'ohlc_low':
          aValue = a.tick_data?.ohlc?.low ?? 0;
          bValue = b.tick_data?.ohlc?.low ?? 0;
          break;
        case 'ohlc_close':
          aValue = a.tick_data?.ohlc?.close ?? 0;
          bValue = b.tick_data?.ohlc?.close ?? 0;
          break;
        case 'market_cap':
          aValue = aDerived.marketCap;
          bValue = bDerived.marketCap;
          break;
        case 'price_to_earning':
          aValue = aDerived.pe;
          bValue = bDerived.pe;
          break;
        case 'return_1day':
          aValue = aDerived.return1d;
          bValue = bDerived.return1d;
          break;
        case 'return_1week':
          aValue = aDerived.return1w;
          bValue = bDerived.return1w;
          break;
        case 'return_1month':
          aValue = aDerived.return1m;
          bValue = bDerived.return1m;
          break;
        case 'bid_ask_spread':
          aValue = aDerived.bidAskSpread;
          bValue = bDerived.bidAskSpread;
          break;
        case 'esm_stage':
          aValue = a.esm_info?.is_in_esm ? (a.esm_info.current_stage ?? 0) : -1;
          bValue = b.esm_info?.is_in_esm ? (b.esm_info.current_stage ?? 0) : -1;
          break;
        case 'gsm_stage':
          aValue = a.gsm_info?.is_in_gsm ? parseInt(a.gsm_info.current_stage ?? '0', 10) : -1;
          bValue = b.gsm_info?.is_in_gsm ? parseInt(b.gsm_info.current_stage ?? '0', 10) : -1;
          break;
        default:
          aValue = a.volume_traded;
          bValue = b.volume_traded;
      }

      // Handle null or undefined values
      if ((aValue === null || aValue === undefined) && (bValue === null || bValue === undefined)) return 0;
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      const compareResult = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortDirection === 'asc' ? compareResult : -compareResult;
    });
  }, [snapshots, searchQuery, filters, sortField, sortDirection, applyFilter, getDerivedValues, esmFilter, gsmFilter]);

  // Sort indicator component
  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <span className="text-zinc-400 ml-1 opacity-50">
          ⇅
        </span>
      );
    }
    
    return (
      <span className="text-emerald-500 ml-1">
        {sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
      </span>
    );
  };

  // Add a new filter
  const addFilter = () => {
    if (!newFilterValue || isNaN(parseFloat(newFilterValue))) return;
    
    const newFilter: ScreenerFilter = {
      id: generateFilterId(),
      field: newFilterField,
      operator: newFilterOperator,
      value: parseFloat(newFilterValue),
    };
    
    setFilters([...filters, newFilter]);
    setNewFilterValue('');
  };

  // Remove a filter
  const removeFilter = (id: string) => {
    setFilters(filters.filter(f => f.id !== id));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters([]);
    setSearchQuery('');
    setEsmFilter('all');
    setGsmFilter('all');
  };

  // Get field label
  const getFieldLabel = (key: string) => {
    const field = FILTERABLE_FIELDS.find(f => f.key === key);
    return field?.label ?? key;
  };

  // CSV Export function - All Data
  const downloadCSV = (filtered: boolean = false) => {
    const dataToExport = filtered ? filteredAndSortedSnapshots : snapshots;
    
    // CSV header row
    const csvHeader = [
      'Trading Symbol',
      'Exchange',
      'Stock Name',
      'Industry',
      'Sector',
      'ISIN',
      'NSE Code',
      'BSE Code',
      'Last Price',
      'Change %',
      'Net Change',
      'Volume Traded',
      'Total Buy Qty',
      'Total Sell Qty',
      'Buy/Sell Ratio',
      'Avg Trade Price',
      'Open',
      'High',
      'Low',
      'Close',
      'Best Bid',
      'Best Ask',
      'Bid-Ask Spread %',
      'Market Cap (Cr)',
      'P/E',
      '1D Return %',
      '1W Return %',
      '1M Return %',
      '3M Return %',
      '6M Return %',
      '1Y Return %',
      'Promoter Holding %',
      'ROE',
      'D/E',
      'EPS',
      'P/B',
      'Down from 52W High %',
      'Is SME',
      'ESM Status',
      'ESM Stage',
      'ESM Entry Date',
      'ESM Exit Date',
      'ESM Transaction Type',
      'GSM Status',
      'GSM Stage',
      'GSM Entry Date',
      'GSM Exit Date',
      'GSM Criteria',
      'TradingView Link',
      'Screener Link',
      'Timestamp',
      'External Code'
    ].join(',');

    // Format data rows
    const csvRows = dataToExport.map(snapshot => {
      const derived = getDerivedValues(snapshot);
      const tickData = snapshot.tick_data;
      const ohlc = tickData?.ohlc;
      const depth = tickData?.depth;
      const stockInfo = snapshot.stock_info;
      const financial = snapshot.financial_info;
      const esmInfo = snapshot.esm_info;
      const gsmInfo = snapshot.gsm_info;
      
      const bestBidPrice = depth?.buy?.[0]?.price ?? '';
      const bestAskPrice = depth?.sell?.[0]?.price ?? '';

      const row = [
        snapshot.trading_symbol,
        snapshot.exchange,
        `"${stockInfo?.stock_name ?? ''}"`,
        `"${stockInfo?.industry_name ?? ''}"`,
        `"${stockInfo?.sector_name ?? ''}"`,
        stockInfo?.isin ?? '',
        stockInfo?.nse_code ?? '',
        stockInfo?.bse_code ?? '',
        snapshot.last_price?.toFixed(2) ?? '',
        derived.changePercent?.toFixed(2) ?? '',
        tickData?.net_change?.toFixed(2) ?? '',
        snapshot.volume_traded ?? '',
        tickData?.total_buy_quantity ?? '',
        tickData?.total_sell_quantity ?? '',
        derived.buySellRatio?.toFixed(2) ?? '',
        tickData?.average_trade_price?.toFixed(2) ?? '',
        ohlc?.open?.toFixed(2) ?? '',
        ohlc?.high?.toFixed(2) ?? '',
        ohlc?.low?.toFixed(2) ?? '',
        ohlc?.close?.toFixed(2) ?? '',
        bestBidPrice,
        bestAskPrice,
        derived.bidAskSpread?.toFixed(4) ?? '',
        financial?.market_capitalization?.toFixed(2) ?? '',
        financial?.price_to_earning?.toFixed(2) ?? '',
        financial?.return_over_1day?.toFixed(2) ?? '',
        financial?.return_over_1week?.toFixed(2) ?? '',
        financial?.return_over_1month?.toFixed(2) ?? '',
        financial?.return_over_3months?.toFixed(2) ?? '',
        financial?.return_over_6months?.toFixed(2) ?? '',
        financial?.return_over_1year?.toFixed(2) ?? '',
        financial?.promoter_holding?.toFixed(2) ?? '',
        financial?.return_on_equity?.toFixed(2) ?? '',
        financial?.debt_to_equity?.toFixed(2) ?? '',
        financial?.eps?.toFixed(2) ?? '',
        financial?.price_to_book_value?.toFixed(2) ?? '',
        financial?.down_from_52w_high?.toFixed(2) ?? '',
        financial?.is_sme ? 'Yes' : 'No',
        // ESM Info
        esmInfo?.is_in_esm ? 'Yes' : 'No',
        esmInfo?.is_in_esm ? (esmInfo.current_stage ?? '') : '',
        esmInfo?.entry_date ?? '',
        esmInfo?.last_exit_date ?? '',
        esmInfo?.transaction_type ?? '',
        // GSM Info
        gsmInfo?.is_in_gsm ? 'Yes' : 'No',
        gsmInfo?.is_in_gsm ? (gsmInfo.current_stage ?? '') : '',
        gsmInfo?.entry_date ?? '',
        gsmInfo?.last_exit_date ?? '',
        gsmInfo?.criteria_name ?? '',
        // Links and External Code
        stockInfo?.trading_view_navlink ?? '',
        stockInfo?.screener_navlink ?? '',
        tickData?.timestamp ?? '',
        stockInfo?.stock_code ? `"${stockInfo.stock_code},"` : ''
      ].join(',');
      return row;
    });

    // Combine header and rows
    const csvContent = [csvHeader, ...csvRows].join('\n');

    // Create a Blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const filename = filtered 
      ? `tick-analysis-filtered-${new Date().toISOString().split('T')[0]}.csv`
      : `tick-analysis-all-${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 table-container-with-popups">
      <style jsx>{`
        .underline-on-hover:hover {
          text-decoration: underline;
        }
      `}</style>
      
      {/* Header with counts and download */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-zinc-900 dark:text-white">
            Market Depth Analysis
          </h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Showing {filteredAndSortedSnapshots.length} of {snapshots.length} instruments
            {filters.length > 0 && ` (${filters.length} filter${filters.length > 1 ? 's' : ''} applied)`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => downloadCSV(true)}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export Filtered
          </Button>
          <Button
            onClick={() => downloadCSV(false)}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export All
          </Button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 p-4 space-y-4">
        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="text"
              placeholder="Search by symbol, name, industry, sector..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* ESM Filter */}
          <div className="w-36">
            <select
              value={esmFilter}
              onChange={(e) => setEsmFilter(e.target.value as ESMFilterOption)}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 ${
                esmFilter !== 'all'
                  ? 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-800 dark:text-red-200'
                  : 'bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white'
              }`}
            >
              <option value="all">All ESM</option>
              <option value="in_esm">In ESM</option>
              <option value="not_in_esm">Not in ESM</option>
              <option value="esm_1">ESM Stage 1</option>
              <option value="esm_2">ESM Stage 2</option>
            </select>
          </div>
          
          {/* GSM Filter */}
          <div className="w-36">
            <select
              value={gsmFilter}
              onChange={(e) => setGsmFilter(e.target.value as GSMFilterOption)}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 ${
                gsmFilter !== 'all'
                  ? 'bg-orange-50 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700 text-orange-800 dark:text-orange-200'
                  : 'bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white'
              }`}
            >
              <option value="all">All GSM</option>
              <option value="in_gsm">In GSM</option>
              <option value="not_in_gsm">Not in GSM</option>
            </select>
          </div>
          
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-colors ${
              showFilterPanel || filters.length > 0
                ? 'border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800'
            }`}
          >
            <Filter className="h-4 w-4" />
            Screener Filters
            {filters.length > 0 && (
              <span className="bg-emerald-600 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                {filters.length}
              </span>
            )}
          </button>
          {(filters.length > 0 || searchQuery || esmFilter !== 'all' || gsmFilter !== 'all') && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Screener Filter Panel */}
        {showFilterPanel && (
          <div className="border-t border-zinc-200 dark:border-zinc-800 pt-4 space-y-4">
            <div className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Add Screening Criteria
            </div>
            
            {/* Add Filter Form */}
            <div className="flex flex-wrap gap-3 items-end">
              {/* Field Dropdown */}
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">
                  Field
                </label>
                <select
                  value={newFilterField}
                  onChange={(e) => setNewFilterField(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                >
                  <optgroup label="General">
                    {FILTERABLE_FIELDS.filter(f => f.category === 'general').map(field => (
                      <option key={field.key} value={field.key}>{field.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Tick Data">
                    {FILTERABLE_FIELDS.filter(f => f.category === 'tick').map(field => (
                      <option key={field.key} value={field.key}>{field.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="OHLC">
                    {FILTERABLE_FIELDS.filter(f => f.category === 'ohlc').map(field => (
                      <option key={field.key} value={field.key}>{field.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Market Depth">
                    {FILTERABLE_FIELDS.filter(f => f.category === 'depth').map(field => (
                      <option key={field.key} value={field.key}>{field.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Financial">
                    {FILTERABLE_FIELDS.filter(f => f.category === 'financial').map(field => (
                      <option key={field.key} value={field.key}>{field.label}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
              
              {/* Operator Dropdown */}
              <div className="w-24">
                <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">
                  Condition
                </label>
                <select
                  value={newFilterOperator}
                  onChange={(e) => setNewFilterOperator(e.target.value as FilterOperator)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                >
                  <option value=">">&gt;</option>
                  <option value="<">&lt;</option>
                  <option value=">=">&gt;=</option>
                  <option value="<=">&lt;=</option>
                  <option value="=">=</option>
                  <option value="!=">!=</option>
                </select>
              </div>
              
              {/* Value Input */}
              <div className="w-32">
                <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">
                  Value
                </label>
                <input
                  type="number"
                  step="any"
                  value={newFilterValue}
                  onChange={(e) => setNewFilterValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addFilter()}
                  placeholder="0.00"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>
              
              {/* Add Button */}
              <button
                onClick={addFilter}
                disabled={!newFilterValue || isNaN(parseFloat(newFilterValue))}
                className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>

            {/* Active Filters */}
            {filters.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {filters.map(filter => (
                  <div
                    key={filter.id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-full text-sm"
                  >
                    <span className="font-medium">{getFieldLabel(filter.field)}</span>
                    <span className="text-emerald-600 dark:text-emerald-300">{filter.operator}</span>
                    <span>{filter.value}</span>
                    <button
                      onClick={() => removeFilter(filter.id)}
                      className="ml-1 text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-200"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Data Table */}
      <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900 overflow-hidden table-wrapper-popup-safe min-table-height">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-zinc-400 scrollbar-track-zinc-200 dark:scrollbar-thumb-zinc-600 dark:scrollbar-track-zinc-800">
          <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
            <thead className="bg-zinc-50 dark:bg-zinc-800 sticky top-0 z-[2]">
              <tr>
                {/* Sticky Symbol Column */}
                <th
                  className="sticky left-0 z-[3] px-6 py-4 text-left text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200 bg-zinc-50 dark:bg-zinc-800 shadow-sm min-w-[220px]"
                  onClick={() => handleSort('trading_symbol')}
                >
                  <div className="flex items-center gap-1">
                    Symbol / Stock
                    <SortIndicator field="trading_symbol" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('last_price')}
                >
                  <div className="flex items-center justify-end gap-1">
                    LTP
                    <SortIndicator field="last_price" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('change_percent')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Change %
                    <SortIndicator field="change_percent" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('volume_traded')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Volume
                    <SortIndicator field="volume_traded" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('total_buy_quantity')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Buy Qty
                    <SortIndicator field="total_buy_quantity" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('total_sell_quantity')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Sell Qty
                    <SortIndicator field="total_sell_quantity" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('buy_sell_ratio')}
                >
                  <div className="flex items-center justify-end gap-1">
                    B/S Ratio
                    <SortIndicator field="buy_sell_ratio" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('bid_ask_spread')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Spread %
                    <SortIndicator field="bid_ask_spread" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('ohlc_open')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Open
                    <SortIndicator field="ohlc_open" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('ohlc_high')}
                >
                  <div className="flex items-center justify-end gap-1">
                    High
                    <SortIndicator field="ohlc_high" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('ohlc_low')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Low
                    <SortIndicator field="ohlc_low" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('ohlc_close')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Close
                    <SortIndicator field="ohlc_close" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('market_cap')}
                >
                  <div className="flex items-center justify-end gap-1">
                    MCap
                    <SortIndicator field="market_cap" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('price_to_earning')}
                >
                  <div className="flex items-center justify-end gap-1">
                    P/E
                    <SortIndicator field="price_to_earning" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('return_1day')}
                >
                  <div className="flex items-center justify-end gap-1">
                    1D %
                    <SortIndicator field="return_1day" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('return_1week')}
                >
                  <div className="flex items-center justify-end gap-1">
                    1W %
                    <SortIndicator field="return_1week" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-right text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('return_1month')}
                >
                  <div className="flex items-center justify-end gap-1">
                    1M %
                    <SortIndicator field="return_1month" />
                  </div>
                </th>

                <th className="px-4 py-4 text-left text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                  Industry
                </th>

                <th
                  className="px-4 py-4 text-center text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('esm_stage')}
                >
                  <div className="flex items-center justify-center gap-1">
                    ESM
                    <SortIndicator field="esm_stage" />
                  </div>
                </th>

                <th
                  className="px-4 py-4 text-center text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50 transition-all duration-200"
                  onClick={() => handleSort('gsm_stage')}
                >
                  <div className="flex items-center justify-center gap-1">
                    GSM
                    <SortIndicator field="gsm_stage" />
                  </div>
                </th>

                <th className="px-4 py-4 text-center text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-zinc-900 divide-y divide-zinc-200 dark:divide-zinc-800">
              {filteredAndSortedSnapshots.map((snapshot) => {
                const derived = getDerivedValues(snapshot);
                const tickData = snapshot.tick_data;
                const ohlc = tickData?.ohlc;
                const stockInfo = snapshot.stock_info;
                const changeFormatted = formatPercentage(derived.changePercent);
                const return1dFormatted = formatPercentage(derived.return1d);
                const return1wFormatted = formatPercentage(derived.return1w);
                const return1mFormatted = formatPercentage(derived.return1m);

                return (
                  <tr
                    key={`${snapshot.id}-${snapshot.exchange}`}
                    className="group transition-colors table-row-with-menu hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                  >
                    {/* Sticky Symbol Column */}
                    <td className="sticky left-0 z-[1] px-6 py-3 whitespace-nowrap min-w-[220px] shadow-sm bg-white dark:bg-zinc-900 group-hover:bg-zinc-50 dark:group-hover:bg-zinc-800">
                      <div className="flex-1">
                        {stockInfo && (
                          <div className="mb-2 relative z-[10]">
                            <StockActionIcons
                              isin={stockInfo.isin}
                              stockName={stockInfo.stock_name}
                              tradingViewLink={stockInfo.trading_view_navlink}
                              screenerLink={stockInfo.screener_navlink}
                              currentPrice={snapshot.last_price}
                              onStockDetailClick={setSelectedStock}
                              showNotesCount
                            />
                          </div>
                        )}
                        <div
                          className="cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                          onClick={() => stockInfo && setSelectedStock(stockInfo.isin)}
                          role="button"
                          aria-label={`View details for ${stockInfo?.stock_name || snapshot.trading_symbol}`}
                        >
                          <div className="text-sm font-semibold text-zinc-900 dark:text-white underline-on-hover">
                            {snapshot.trading_symbol}
                          </div>
                          {stockInfo && (
                            <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate max-w-[180px]">
                              {stockInfo.stock_name}
                            </div>
                          )}
                        </div>
                        {!snapshot.is_matched && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400 mt-1">
                            Unmatched
                          </span>
                        )}
                      </div>
                    </td>

                    {/* LTP */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-zinc-900 dark:text-white">
                      ₹{snapshot.last_price?.toFixed(2) ?? 'N/A'}
                    </td>

                    {/* Change % */}
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${changeFormatted.color}`}>
                      {changeFormatted.text}
                    </td>

                    {/* Volume */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-zinc-700 dark:text-zinc-300">
                      {formatVolume(snapshot.volume_traded)}
                    </td>

                    {/* Buy Qty */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600 dark:text-green-400">
                      {formatVolume(tickData?.total_buy_quantity)}
                    </td>

                    {/* Sell Qty */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-red-600 dark:text-red-400">
                      {formatVolume(tickData?.total_sell_quantity)}
                    </td>

                    {/* B/S Ratio */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                        !derived.buySellRatio ? 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400' :
                        derived.buySellRatio >= 2 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        derived.buySellRatio >= 1 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {derived.buySellRatio?.toFixed(2) ?? 'N/A'}
                      </span>
                    </td>

                    {/* Spread % */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-zinc-700 dark:text-zinc-300">
                      {derived.bidAskSpread?.toFixed(3) ?? 'N/A'}%
                    </td>

                    {/* OHLC */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-zinc-700 dark:text-zinc-300">
                      {ohlc?.open?.toFixed(2) ?? 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600 dark:text-green-400">
                      {ohlc?.high?.toFixed(2) ?? 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-red-600 dark:text-red-400">
                      {ohlc?.low?.toFixed(2) ?? 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-zinc-700 dark:text-zinc-300">
                      {ohlc?.close?.toFixed(2) ?? 'N/A'}
                    </td>

                    {/* Market Cap */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-zinc-700 dark:text-zinc-300">
                      {formatNumber(derived.marketCap)}
                    </td>

                    {/* P/E */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-zinc-700 dark:text-zinc-300">
                      {derived.pe?.toFixed(2) ?? 'N/A'}
                    </td>

                    {/* Returns */}
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${return1dFormatted.color}`}>
                      {return1dFormatted.text}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${return1wFormatted.color}`}>
                      {return1wFormatted.text}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${return1mFormatted.color}`}>
                      {return1mFormatted.text}
                    </td>

                    {/* Industry */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-zinc-600 dark:text-zinc-400">
                      <span className="text-xs bg-zinc-100 dark:bg-zinc-800 px-2 py-1 rounded-md truncate max-w-[150px] inline-block">
                        {stockInfo?.industry_name ?? 'N/A'}
                      </span>
                    </td>

                    {/* ESM Status */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {snapshot.esm_info?.is_in_esm ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                          snapshot.esm_info.current_stage === 2 
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200' 
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200'
                        }`}>
                          <AlertTriangle className="h-3 w-3" />
                          ESM {snapshot.esm_info.current_stage}
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-400 dark:text-zinc-500">-</span>
                      )}
                    </td>

                    {/* GSM Status */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {snapshot.gsm_info?.is_in_gsm ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                          parseInt(snapshot.gsm_info.current_stage ?? '0', 10) >= 2 
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200'
                            : parseInt(snapshot.gsm_info.current_stage ?? '0', 10) >= 1
                              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
                        }`}>
                          <AlertTriangle className="h-3 w-3" />
                          GSM {snapshot.gsm_info.current_stage}
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-400 dark:text-zinc-500">-</span>
                      )}
                    </td>

                    {/* Actions - View Button */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSnapshot(snapshot);
                        }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg transition-colors"
                        title="View complete details"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredAndSortedSnapshots.length === 0 && (
          <div className="py-16 text-center">
            <div className="text-zinc-400 dark:text-zinc-500 text-lg">
              No instruments match your criteria
            </div>
            <p className="text-zinc-500 dark:text-zinc-400 text-sm mt-2">
              Try adjusting your search or filters
            </p>
          </div>
        )}
      </div>

      {/* Stock Detail Modal */}
      {selectedStock && (
        <StockDetailModal 
          isin={selectedStock}
          onClose={() => setSelectedStock(null)}
        />
      )}

      {/* Tick Snapshot Detail Modal */}
      {selectedSnapshot && (
        <TickSnapshotDetailModal
          snapshot={selectedSnapshot}
          onClose={() => setSelectedSnapshot(null)}
        />
      )}
    </div>
  );
}

